import React, {
  useEffect,
  useRef,
  useCallback,
  type CSSProperties,
} from "react";

import "./IndustrialAnimatedBackground.css";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  phase: number;
}

interface NetworkNode {
  x: number;
  y: number;
  radius: number;
  phase: number;
}

interface Pulse {
  pathIndex: number;
  progress: number;
  speed: number;
}

interface MousePosition {
  x: number;
  y: number;
  active: boolean;
}

const PARTICLE_COUNT_DESKTOP = 55;
const PARTICLE_COUNT_MOBILE = 28;
const NODE_COUNT = 22;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const random = (min: number, max: number): number =>
  Math.random() * (max - min) + min;

export const IndustrialAnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const mouseRef = useRef<MousePosition>({
    x: 0.5,
    y: 0.5,
    active: false,
  });

  const animationFrameRef = useRef<number | null>(null);

  const targetParallaxRef = useRef({
    x: 0,
    y: 0,
  });

  const currentParallaxRef = useRef({
    x: 0,
    y: 0,
  });

  const prefersReducedMotionRef = useRef(false);

  const initializeParticles = useCallback(
    (width: number, height: number): Particle[] => {
      const isMobile = width < 768;
      const count = isMobile ? PARTICLE_COUNT_MOBILE : PARTICLE_COUNT_DESKTOP;
      return Array.from({ length: count }, () => ({
        x: random(0, width),
        y: random(0, height),
        vx: random(-0.08, 0.08),
        vy: random(-0.04, 0.04),
        size: random(0.5, 1.7),
        alpha: random(0.12, 0.45),
        phase: random(0, Math.PI * 2),
      }));
    },
    []
  );

  const initializeNodes = useCallback(
    (width: number, height: number): NetworkNode[] => {
      return Array.from({ length: NODE_COUNT }, (_, index) => ({
        x: random(width * 0.05, width * 0.95),
        y: random(height * 0.08, height * 0.92),
        radius: index % 5 === 0 ? 2.8 : random(1.2, 2.2),
        phase: random(0, Math.PI * 2),
      }));
    },
    []
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d", {
      alpha: true,
      desynchronized: true,
    });
    if (!context) return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    prefersReducedMotionRef.current = mediaQuery.matches;
    const handleMotionPreference = (event: MediaQueryListEvent) => {
      prefersReducedMotionRef.current = event.matches;
    };
    mediaQuery.addEventListener("change", handleMotionPreference);

    let width = 0;
    let height = 0;
    let particles: Particle[] = [];
    let nodes: NetworkNode[] = [];

    const pulses: Pulse[] = [
      { pathIndex: 0, progress: 0.15, speed: 0.00035 },
      { pathIndex: 1, progress: 0.65, speed: 0.00025 },
      { pathIndex: 2, progress: 0.35, speed: 0.0003 },
      { pathIndex: 3, progress: 0.8, speed: 0.00022 },
    ];

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = initializeParticles(width, height);
      nodes = initializeNodes(width, height);
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const normalizedX = (event.clientX - rect.left) / rect.width;
      const normalizedY = (event.clientY - rect.top) / rect.height;
      mouseRef.current.x = clamp(normalizedX, 0, 1);
      mouseRef.current.y = clamp(normalizedY, 0, 1);
      mouseRef.current.active = true;
      targetParallaxRef.current.x = (normalizedX - 0.5) * 18;
      targetParallaxRef.current.y = (normalizedY - 0.5) * 12;
      canvas.style.setProperty("--mouse-x", `${normalizedX * 100}%`);
      canvas.style.setProperty("--mouse-y", `${normalizedY * 100}%`);
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
      targetParallaxRef.current.x = 0;
      targetParallaxRef.current.y = 0;
    };

    const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
    if (!isCoarsePointer) {
      window.addEventListener("mousemove", handleMouseMove, { passive: true });
      window.addEventListener("mouseleave", handleMouseLeave);
    }

    const drawBackgroundGlow = (time: number) => {
      const gradientX = width * (0.5 + Math.sin(time * 0.00015) * 0.08);
      const gradientY = height * (0.45 + Math.cos(time * 0.00012) * 0.08);
      const gradient = context.createRadialGradient(
        gradientX, gradientY, 0, gradientX, gradientY, Math.max(width, height) * 0.75
      );
      gradient.addColorStop(0, "rgba(52, 103, 255, 0.10)");
      gradient.addColorStop(0.45, "rgba(91, 47, 190, 0.045)");
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);
    };

    const drawNetwork = (time: number) => {
      const mouse = mouseRef.current;
      for (let i = 0; i < nodes.length; i++) {
        const nodeA = nodes[i];
        if (!nodeA) continue;
        for (let j = i + 1; j < nodes.length; j++) {
          const nodeB = nodes[j];
          if (!nodeB) continue;
          const dx = nodeA.x - nodeB.x;
          const dy = nodeA.y - nodeB.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = Math.min(width, height) * 0.23;
          if (distance > maxDistance) continue;
          
          const mouseX = mouse.x * width;
          const mouseY = mouse.y * height;
          const distanceFromMouse = Math.sqrt(Math.pow(nodeA.x - mouseX, 2) + Math.pow(nodeA.y - mouseY, 2));
          const mouseInfluence = clamp(1 - distanceFromMouse / 220, 0, 1);
          const opacity = 0.035 + mouseInfluence * 0.12;

          context.beginPath();
          context.moveTo(nodeA.x, nodeA.y);
          context.lineTo(nodeB.x, nodeB.y);
          context.strokeStyle = `rgba(76, 156, 255, ${opacity})`;
          context.lineWidth = mouseInfluence > 0.1 ? 0.8 : 0.45;
          context.stroke();
        }
      }

      nodes.forEach((node) => {
        const pulse = 0.5 + Math.sin(time * 0.001 + node.phase) * 0.5;
        const nodeX = node.x;
        const nodeY = node.y;
        const mouseX = mouse.x * width;
        const mouseY = mouse.y * height;
        const distanceFromMouse = Math.sqrt(Math.pow(nodeX - mouseX, 2) + Math.pow(nodeY - mouseY, 2));
        const influence = clamp(1 - distanceFromMouse / 180, 0, 1);

        if (influence > 0.01) {
          context.beginPath();
          context.arc(nodeX, nodeY, 7 + influence * 10, 0, Math.PI * 2);
          context.fillStyle = `rgba(57, 180, 255, ${0.025 * influence})`;
          context.fill();
        }

        context.beginPath();
        context.arc(nodeX, nodeY, node.radius + pulse * 0.4 + influence * 0.8, 0, Math.PI * 2);
        context.fillStyle = `rgba(80, 190, 255, ${0.25 + pulse * 0.15 + influence * 0.3})`;
        context.fill();
      });
    };

    const drawParticles = () => {
      const reduceMotion = prefersReducedMotionRef.current;
      particles.forEach((particle) => {
        if (!reduceMotion) {
          particle.x += particle.vx;
          particle.y += particle.vy;
        }

        if (particle.x < -10) particle.x = width + 10;
        if (particle.x > width + 10) particle.x = -10;
        if (particle.y < -10) particle.y = height + 10;
        if (particle.y > height + 10) particle.y = -10;

        context.beginPath();
        context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        context.fillStyle = `rgba(115, 186, 255, ${particle.alpha})`;
        context.fill();
      });
    };

    const drawCursorGlow = () => {
      if (!mouseRef.current.active) return;
      const mouseX = mouseRef.current.x * width;
      const mouseY = mouseRef.current.y * height;
      const gradient = context.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, 190);
      gradient.addColorStop(0, "rgba(54, 178, 255, 0.08)");
      gradient.addColorStop(0.35, "rgba(100, 80, 255, 0.035)");
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      context.fillStyle = gradient;
      context.fillRect(mouseX - 190, mouseY - 190, 380, 380);
    };

    const drawProcessPulses = (time: number) => {
      const processPaths: { points: [number, number][] }[] = [
        { points: [[width * 0.02, height * 0.32], [width * 0.17, height * 0.32], [width * 0.21, height * 0.39], [width * 0.34, height * 0.39]] },
        { points: [[width * 0.97, height * 0.23], [width * 0.82, height * 0.23], [width * 0.77, height * 0.31], [width * 0.65, height * 0.31]] },
        { points: [[width * 0.08, height * 0.76], [width * 0.25, height * 0.76], [width * 0.29, height * 0.69], [width * 0.41, height * 0.69]] },
        { points: [[width * 0.96, height * 0.72], [width * 0.80, height * 0.72], [width * 0.76, height * 0.66], [width * 0.62, height * 0.66]] },
      ];

      pulses.forEach((pulse) => {
        const path = processPaths[pulse.pathIndex];
        if (!path) return;
        
        if (!prefersReducedMotionRef.current) {
          pulse.progress += pulse.speed;
          if (pulse.progress > 1) pulse.progress = 0;
        }

        const segment = Math.floor(pulse.progress * (path.points.length - 1));
        const localProgress = pulse.progress * (path.points.length - 1) - segment;
        const start = path.points[segment] as [number, number];
        const end = path.points[Math.min(segment + 1, path.points.length - 1)] as [number, number];
        const x = start[0] + (end[0] - start[0]) * localProgress;
        const y = start[1] + (end[1] - start[1]) * localProgress;

        context.beginPath();
        context.arc(x, y, 2.8, 0, Math.PI * 2);
        context.fillStyle = "rgba(74, 199, 255, 0.85)";
        context.shadowBlur = 14;
        context.shadowColor = "rgba(74, 199, 255, 0.8)";
        context.fill();
        context.shadowBlur = 0;
      });

      const ambient = 0.5 + Math.sin(time * 0.0008) * 0.5;
      context.fillStyle = `rgba(100, 80, 255, ${0.012 * ambient})`;
      context.fillRect(0, 0, width, height);
    };

    let lastFpsTime = performance.now();
    let frameCount = 0;
    let fpsHistory: number[] = [];
    let isLowPerformance = false;

    const render = (time: number) => {
      frameCount++;
      const now = performance.now();
      if (now - lastFpsTime >= 1000) {
        const fps = (frameCount * 1000) / (now - lastFpsTime);
        fpsHistory.push(fps);
        if (fpsHistory.length > 5) fpsHistory.shift();
        
        if (!isLowPerformance && fpsHistory.length >= 3) {
          const avgFps = fpsHistory.reduce((a, b) => a + b, 0) / fpsHistory.length;
          if (avgFps < 35) {
            isLowPerformance = true;
            particles = particles.slice(0, Math.floor(particles.length / 3));
          }
        }
        lastFpsTime = now;
        frameCount = 0;
      }

      context.clearRect(0, 0, width, height);
      const current = currentParallaxRef.current;
      const target = prefersReducedMotionRef.current ? { x: 0, y: 0 } : targetParallaxRef.current;
      current.x += (target.x - current.x) * 0.045;
      current.y += (target.y - current.y) * 0.045;

      drawBackgroundGlow(time);

      if (!isLowPerformance) {
        context.save();
        context.translate(current.x * 0.15, current.y * 0.15);
        drawNetwork(time);
        context.restore();
      }

      context.save();
      context.translate(current.x * 0.4, current.y * 0.4);
      drawParticles();
      context.restore();

      context.save();
      context.translate(current.x * 0.6, current.y * 0.6);
      drawProcessPulses(time);
      context.restore();

      drawCursorGlow();

      animationFrameRef.current = requestAnimationFrame(render);
    };

    animationFrameRef.current = requestAnimationFrame(render);

    return () => {
      resizeObserver.disconnect();
      if (!isCoarsePointer) {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseleave", handleMouseLeave);
      }
      mediaQuery.removeEventListener("change", handleMotionPreference);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [initializeParticles, initializeNodes]);

  const staticStyle = { "--industrial-opacity": "1" } as CSSProperties;

  return (
    <div className="industrial-background" aria-hidden="true" style={staticStyle}>
      <div className="industrial-gradient" />
      <div className="industrial-grid" />
      <div className="industrial-grid industrial-grid--secondary" />
      <div className="industrial-glow industrial-glow--left" />
      <div className="industrial-glow industrial-glow--right" />
      <div className="industrial-parallax industrial-parallax--slow">
        <svg className="industrial-lines" viewBox="0 0 1600 900" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#35c8ff" stopOpacity="0" />
              <stop offset="50%" stopColor="#35c8ff" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#9b6cff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0 280 H160 L205 325 H340 L390 280 H470" className="industrial-path" />
          <path d="M0 410 H110 L145 375 H285 L325 415 H440" className="industrial-path" />
          <path d="M40 680 H220 L270 625 H405 L455 680 H520" className="industrial-path" />
          <path d="M1600 200 H1450 L1400 250 H1260 L1210 205 H1120" className="industrial-path" />
          <path d="M1600 390 H1490 L1450 350 H1320 L1270 400 H1150" className="industrial-path" />
          <path d="M1600 690 H1460 L1405 635 H1270 L1220 690 H1090" className="industrial-path" />
          <path d="M100 90 H430 L475 45 H720" className="industrial-path industrial-path--faint" />
          <path d="M1500 100 H1220 L1170 150 H900" className="industrial-path industrial-path--faint" />
          <path d="M0 805 H280 L320 765 H610" className="industrial-path industrial-path--faint" />
          <path d="M1600 805 H1320 L1270 765 H990" className="industrial-path industrial-path--faint" />
          <g className="industrial-nodes">
            <circle cx="205" cy="325" r="5" />
            <circle cx="340" cy="325" r="4" />
            <circle cx="145" cy="375" r="4" />
            <circle cx="270" cy="625" r="5" />
            <circle cx="1400" cy="250" r="5" />
            <circle cx="1270" cy="400" r="4" />
            <circle cx="1405" cy="635" r="5" />
          </g>
        </svg>
      </div>
      <div className="industrial-parallax industrial-parallax--medium">
        <div className="industrial-factory industrial-factory--left">
          <div className="factory-building factory-building--one" />
          <div className="factory-building factory-building--two" />
          <div className="factory-chimney" />
          <div className="factory-roof" />
        </div>
        <div className="industrial-conveyor industrial-conveyor--left">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className="industrial-conveyor industrial-conveyor--right">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className="industrial-robot industrial-robot--left">
          <div className="robot-base" />
          <div className="robot-arm robot-arm--one" />
          <div className="robot-arm robot-arm--two" />
          <div className="robot-hand" />
        </div>
        <div className="industrial-gear industrial-gear--one">
          ⚙
        </div>
        <div className="industrial-gear industrial-gear--two">
          ⚙
        </div>
      </div>
      <canvas ref={canvasRef} className="industrial-canvas" />
      <div className="industrial-scanline" />
      <div className="industrial-vignette" />
    </div>
  );
};
